import { useEffect, useState } from "react";

import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack
} from "@chakra-ui/react";
// import { useUser } from "@clerk/clerk-react";
import { SignedIn, useAuth } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { Link, useLocation } from "react-router-dom";

import { MenuLink, PrivateSideMenuLinks, SideMenuLinks, SuperuserMenuLink, TopMenuLinks } from "./navLinks";
import { BrowserService } from "../services/browser-service";

import { useAppState } from "~/hooks/useAppState";

interface NavLinkMobileProps {
  item: MenuLink;
}

const NavLinkMobile = ({ item }: NavLinkMobileProps) => {
  const textColor = useColorModeValue("gray.800", "gray.100");
  const hoverBg = useColorModeValue("gray.200", "gray.700");

  return (
    <Link to={item.path} target={item.target}>
      <Box
        px={3}
        py={3}
        ml={3}
        display={"inline-block"}
        fontSize={"sm"}
        rounded={"md"}
        color={textColor}
        _hover={{
          textDecoration: "none",
          color: textColor,
          bg: hoverBg
        }}
      >
        {item.label}
      </Box>
    </Link>
  );
};

interface SideBarProps {
  menuIsVisible: boolean;
  closeMenu: () => void;
}

export default function SideBar({ menuIsVisible, closeMenu }: SideBarProps) {
  const { getButtonProps, getDisclosureProps, isOpen } = useDisclosure({ defaultIsOpen: true });
  const [animationComplete, setAnimationComplete] = useState(true);
  const [showText, setShowText] = useState(true);
  const [allowMobileClicks, setAllowMobileClicks] = useState(false);
  const [isHoveringOverSidebar, setIsHoveringOverSidebar] = useState(false);
  const [linksForUser, setLinksForUser] = useState<MenuLink[]>([]);
  const { userInfo } = useAppState("userInfo");
  const { userId, isSignedIn } = useAuth();

  const bg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const textColor = useColorModeValue("inherit", "gray.100");
  const disabledColor = useColorModeValue("gray.400", "gray.600");

  const isCurrentPath = (path: string) => location.pathname === path;

  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    const links = [...SideMenuLinks];

    // Add Superuser link for superusers at the top of the menu
    if (userInfo?.isSuperAdmin) {
      links.unshift(SuperuserMenuLink); // Add to the beginning of the array
    }

    setLinksForUser(links);
  }, [userInfo]);

  useEffect(() => {
    setShowText(isOpen && animationComplete);
  }, [isOpen, animationComplete]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (menuIsVisible) {
      intervalId = setTimeout(() => {
        setAllowMobileClicks(true);
      }, 500);
    } else {
      setAllowMobileClicks(false);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        setAllowMobileClicks(false);
      }
    };
  }, [menuIsVisible]);

  function handleOnMouseOver(event: React.MouseEvent<HTMLElement>): void {
    if (BrowserService.isTouchDevice) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    setIsHoveringOverSidebar(true);
  }

  function handleOnMouseOut(event: React.MouseEvent<HTMLElement>): void {
    if (BrowserService.isTouchDevice) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    setIsHoveringOverSidebar(false);
  }

  const renderNavLink = (link: MenuLink) => {
    const isDisabled = link.requiresAuth && !isSignedIn;
    const isActive = currentPath === link.path;

    // For disabled links that require authentication
    if (isDisabled) {
      return (
        <Tooltip label={link.disabledTooltip || "Please sign in"} placement="right" hasArrow>
          <HStack
            justify={"flex-start"}
            cursor={"not-allowed"}
            py={2.5}
            pl={4}
            color={disabledColor}
            position={"relative"}
            opacity={0.7}
          >
            <Box pr={2} fontSize={"1.6rem"} position={"relative"} top={"5px"} left={1} color={disabledColor}>
              {link.icon}
            </Box>
            <Text fontSize="sm" align={"center"} color={disabledColor}>
              {link.label}
            </Text>
          </HStack>
        </Tooltip>
      );
    }

    // For active links
    return (
      <Link to={link.path}>
        <HStack
          justify={"flex-start"}
          cursor={"pointer"}
          py={2.5}
          pl={4}
          color="brand.primary"
          bgColor={isActive ? "brand.primary" : "inherit"}
          position={"relative"}
          _hover={!isActive ? { bg: hoverBg } : undefined}
        >
          <Box
            pr={2}
            fontSize={"1.6rem"}
            position={"relative"}
            top={"5px"}
            left={1}
            color={isActive ? "white" : "brand.primary"}
          >
            {link.icon}
          </Box>
          <Text fontSize="sm" align={"center"} color={isActive ? "white" : textColor}>
            {link.label}
          </Text>
        </HStack>
      </Link>
    );
  };

  // Update the mobile drawer section
  const mobileDrawerContent = (
    <Box pt={5}>
      <VStack as={"nav"} spacing={1} align={"left"} justify={"flex-end"} px=".5rem">
        {TopMenuLinks.map((link) => (
          <NavLinkMobile key={link.label} item={link} />
        ))}
      </VStack>
      <VStack align={"left"} pt={3} pl={0} gap={0}>
        <Divider />
        {linksForUser.map((link) => (
          <Box key={link.path}>
            {renderNavLink(link)}
            <Divider />
          </Box>
        ))}
        <SignedIn>
          {PrivateSideMenuLinks.map((link) => (
            <Box key={link.path}>
              {renderNavLink(link)}
              <Divider />
            </Box>
          ))}
        </SignedIn>
      </VStack>
    </Box>
  );

  // Update the desktop sidebar section
  const renderDesktopNavLink = (link: MenuLink) => {
    const isDisabled = link.requiresAuth && !isSignedIn;

    if (isDisabled) {
      return (
        <Tooltip key={link.label} label={link.disabledTooltip || "Please sign in"} placement="right" hasArrow>
          <HStack justify={"flex-start"} position={"relative"} cursor={"not-allowed"} p={3} pl={".6rem"} opacity={0.7}>
            <motion.div
              style={{ position: "relative" }}
              animate={{
                left: isOpen ? 3 : 6,
                top: isOpen ? 3 : 5,
                fontSize: isOpen ? "1.4rem" : "1.6rem"
              }}
              initial={false}
            >
              <Box pr={2} color={disabledColor}>
                {link.icon}
              </Box>
            </motion.div>
            {showText && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <Text fontSize="sm" align={"center"} color={disabledColor}>
                  {link.label}
                </Text>
              </motion.div>
            )}
          </HStack>
        </Tooltip>
      );
    }

    return (
      <Link to={link.path} key={link.label}>
        <HStack
          justify={"flex-start"}
          position={"relative"}
          cursor={"pointer"}
          p={3}
          pl={".6rem"}
          bg={currentPath === link.path ? "brand.primary" : "inherit"}
          _hover={currentPath === link.path ? {} : { bgColor: hoverBg }}
        >
          <motion.div
            style={{ position: "relative" }}
            animate={{
              left: isOpen ? 3 : 6,
              top: isOpen ? 3 : 5,
              fontSize: isOpen ? "1.4rem" : "1.6rem"
            }}
            initial={false}
          >
            <Box pr={2} color={currentPath == link.path ? "white" : "brand.primary"}>
              {link.icon}
            </Box>
          </motion.div>
          {showText && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <Text fontSize="sm" align={"center"} color={currentPath === link.path ? "white" : textColor}>
                {link.label}
              </Text>
            </motion.div>
          )}
        </HStack>
      </Link>
    );
  };

  return (
    <Box height="100%">
      {/* For mobile view only */}
      <Drawer isOpen={menuIsVisible} placement="left" onClose={() => closeMenu()} size={"xs"}>
        <DrawerOverlay />
        <DrawerContent bg={bg} p={0} pt={14}>
          <DrawerCloseButton _focusVisible={{ boxShadow: "" }} />

          <DrawerBody p={0} pointerEvents={allowMobileClicks ? "all" : "none"}>
            {mobileDrawerContent}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* For desktop view only */}
      <Box
        hideBelow={"md"}
        position={"relative"}
        bg={bg}
        height="100%"
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}
      >
        <motion.div
          {...getDisclosureProps()}
          hidden={false}
          initial={false}
          onAnimationStart={() => setAnimationComplete(false)}
          onAnimationComplete={() => setAnimationComplete(true)}
          animate={{ width: isOpen ? 160 : 60 }}
          style={{
            overflow: "hidden",
            height: "100%"
          }}
        >
          <Box pt={2} color={"white"}>
            {BrowserService.isTouchDevice && <Box w="100%" h="3.5rem" bg="gray.50" {...getButtonProps()} />}
            <VStack align={"left"} pt={BrowserService.isTouchDevice ? 0 : "3.5rem"} gap={0} h="100%">
              {linksForUser.map((link) => renderDesktopNavLink(link))}
            </VStack>
          </Box>
          {BrowserService.isTouchDevice && <Box w="100%" h="100%" bg="gray.50" {...getButtonProps()} />}
        </motion.div>
        <Box position={"absolute"} top={"35px"} right={"5px"} zIndex={1000}>
          {isHoveringOverSidebar && (
            <button {...getButtonProps()}>
              <Box fontSize={"1.5rem"} color="gray.800" onClick={() => closeMenu()}>
                {isOpen ? <GoSidebarExpand /> : <GoSidebarCollapse />}
              </Box>
            </button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
